import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useGlobal } from '../../Context/GlobalContext';
import { useWorkspace } from '../../Context/WorkspaceContext';
import { useLanguage } from '../../Context/LanguageContext';
import {
  Form,
  Spinner,
  Modal
} from 'react-bootstrap';
import { PrimaryButton, SuccessButton, DangerButton, CancelButton } from '../../Components/Buttons';
import { BsGlobe, BsFolder, BsFileText, BsQuestionCircle, BsGoogle } from 'react-icons/bs';
import { FiCpu, FiInfo } from 'react-icons/fi';
import CustomTooltip from '../../Components/CustomTooltip';
import WorkspaceTrainingTour from '../../Components/WorkspaceTrainingTour';
import CompactUpgradePrompt from '../../Components/CompactUpgradePrompt';
import { WorkspaceTrainingSkeleton } from '../../Components/SkeletonLoaders';
import PageHeader from '../../Components/PageHeader';

import TrainingList from './TrainingList';
import TrainingWebsites from './TrainingWebsites';
import AddTextModal from './Modals/AddTextModal';
import AddFilesModal from './Modals/AddFilesModal';
import AddLinksModal from './Modals/AddLinksModal';
import AddWebsiteModal from './Modals/AddWebsiteModal';
import AddQAModal from './Modals/AddQAModal';
import AddGoogleDocsModal from './Modals/AddGoogleDocsModal';
import AddNotionModal from './Modals/AddNotionModal';
import AssignmentModal from './Modals/AssignmentModal';
import TrainProcess from './TrainProcess';
import WorkspaceInfoPanel from '../../Components/WorkspaceInfoPanel';
import SystemStatusMessage from '../../Components/SystemStatusMessage';
import QuickStartGuide from '../../Components/QuickStartGuide';
import './agent-training.css';

// Notion icon component
const NotionIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.017 4.313l55.333 -4.087c6.797 -0.583 8.543 -0.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277 -1.553 6.807 -6.99 7.193L24.467 99.967c-4.08 0.193 -6.023 -0.39 -8.16 -3.113L3.3 79.94c-2.333 -3.113 -3.3 -5.443 -3.3 -8.167V11.113c0 -3.497 1.553 -6.413 6.017 -6.8z" fill="#fff" />
    <path d="M61.35 0.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723 0.967 5.053 3.3 8.167l13.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257 -3.89c5.433 -0.387 6.99 -2.917 6.99 -7.193V20.64c0 -2.21 -0.873 -2.847 -3.443 -4.733L74.167 3.143c-4.273 -3.107 -6.02 -3.5 -12.817 -2.917zM25.92 19.523c-5.247 0.353 -6.437 0.433 -9.417 -1.99L8.927 11.507c-0.77 -0.78 -0.383 -1.753 1.557 -1.947l53.193 -3.887c4.467 -0.39 6.793 1.167 8.54 2.527l9.123 6.61c0.39 0.197 1.36 1.36 0.193 1.36l-54.933 3.307 -0.68 0.047zM19.803 88.3V30.367c0 -2.53 0.777 -3.697 3.103 -3.893L86 22.80c2.14 -0.193 3.107 1.167 3.107 3.693v57.547c0 2.53 -0.39 4.67 -3.883 4.863l-60.377 3.5c-3.493 0.193 -5.043 -0.97 -5.043 -4.083zm59.6 -54.827c0.387 1.75 0 3.5 -1.75 3.7l-2.91 0.577v42.773c-2.527 1.36 -4.853 2.137 -6.797 2.137 -3.107 0 -3.883 -0.973 -6.21 -3.887l-19.03 -29.94v28.967l6.02 1.363s0 3.5 -4.857 3.5l-13.39 0.777c-0.39 -0.78 0 -2.723 1.357 -3.11l3.497 -0.97v-38.3L30.48 40.667c-0.39 -1.75 0.58 -4.277 3.3 -4.473l14.367 -0.967 19.8 30.327v-26.83l-5.047 -0.58c-0.39 -2.143 1.163 -3.7 3.103 -3.89l13.4 -0.78z" fill="#000" />
  </svg>
);

const WorkspaceTraining = () => {
  const { section } = useParams(); // Only section parameter for workspace training
  const location = useLocation();
  const navigate = useNavigate();
  const { apiUrl } = useGlobal();
  const { currentWorkspace, workspaceData, getJustWorkspace } = useWorkspace();
  const { t } = useLanguage();
  const [userData] = useState(JSON.parse(localStorage.getItem('userData')));

  // Get workspace permissions - All users can access training features
  const isAdmin = true; // Training is available to all workspace members
  const workspaceId = currentWorkspace?.workspace_id;

  // Check if Google Docs and Notion should be disabled based on environment
  const isGoogleDocsDisabled = false;

  // Main states
  const [trainedData, setTrainedData] = useState({
    text: [],
    file: [],
    web: [],
    qa: [],
    googledoc: [],
    notion: []
  });
  const [isTrainedDataLoading, setIsTrainedDataLoading] = useState(true);
  const [readyToTrain, setReadyToTrain] = useState(false);
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState('OK');
  const previousSystemStatusRef = useRef('OK');
  // eslint-disable-next-line no-unused-vars
  const [_error, setError] = useState(null);
  const [quickStartGuideFunctions, setQuickStartGuideFunctions] = useState(null);

  // Pagination metadata per category
  const [paginationMetadata, setPaginationMetadata] = useState({
    text: { totalCount: 0, totalPages: 1, currentPage: 1 },
    file: { totalCount: 0, totalPages: 1, currentPage: 1 },
    web: { totalCount: 0, totalPages: 1, currentPage: 1 },
    qa: { totalCount: 0, totalPages: 1, currentPage: 1 },
    googledoc: { totalCount: 0, totalPages: 1, currentPage: 1 },
    notion: { totalCount: 0, totalPages: 1, currentPage: 1 }
  });

  // Edit mode
  const [editMode, setEditMode] = useState({
    text: false,
    file: false,
    web: false,
    qa: false,
    googledoc: false,
    notion: false
  });
  const [selectedItems, setSelectedItems] = useState({
    text: [],
    file: [],
    web: [],
    qa: [],
    googledoc: [],
    notion: []
  });

  // Pagination & search
  const [currentPage, setCurrentPage] = useState({ text: 1, file: 1, web: 1, qa: 1, googledoc: 1, notion: 1 });
  const [searchTerms, setSearchTerms] = useState({
    text: '',
    file: '',
    web: '',
    qa: '',
    googledoc: '',
    notion: ''
  });
  const [websitePage, setWebsitePage] = useState({});
  const [websiteLinkSearch, setWebsiteLinkSearch] = useState({});
  const [expandedWebsiteKey, setExpandedWebsiteKey] = useState(null);
  const [selectAllLoading, setSelectAllLoading] = useState({});

  // Modals
  const [showAddTextModal, setShowAddTextModal] = useState(false);
  const [showAddFilesModal, setShowAddFilesModal] = useState(false);
  const [showAddLinksModal, setShowAddLinksModal] = useState(false);
  const [showAddWebsiteModal, setShowAddWebsiteModal] = useState(false);
  const [showAddQAModal, setShowAddQAModal] = useState(false);
  const [showAddGoogleDocsModal, setShowAddGoogleDocsModal] = useState(false);
  const [showAddNotionModal, setShowAddNotionModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [initialAssignments, setInitialAssignments] = useState({ chatbots: [], apps: [] });
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // System status & queue
  const [queuePosition, setQueuePosition] = useState(null);
  const [pollingKey, setPollingKey] = useState(0);
  const [stateLoading, setStateLoading] = useState(true);
  const stateLoadedRef = useRef(false);

  // State for character counts (lifted up)
  // eslint-disable-next-line no-unused-vars
  const [_agentCount, setAgentCount] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);

  const [crawledUrlCount, setCrawledUrlCount] = useState(null);

  // Tour state
  const [showTour, setShowTour] = useState(false);

  // Extract limits from workspace data
  const crawlUrlLimit = workspaceData?.workspace_crawl_url_count_limit || null;


  // Get current section from URL, default to 'website' if not specified
  const selectedTab = section || 'website';

  // Dynamic header content based on selected tab
  const getHeaderContent = (tab) => {
    const headerConfigs = {
      website: {
        title: t('dashboard.workspacetraining.websiteTitle'),
        description: t('dashboard.workspacetraining.websiteDescription'),
        icon: <BsGlobe />
      },
      files: {
        title: t('dashboard.workspacetraining.filesTitle'),
        description: t('dashboard.workspacetraining.filesDescription'),
        icon: <BsFolder />
      },
      text: {
        title: t('dashboard.workspacetraining.textTitle'),
        description: t('dashboard.workspacetraining.textDescription'),
        icon: <BsFileText />
      },
      qa: {
        title: t('dashboard.workspacetraining.qaTitle'),
        description: t('dashboard.workspacetraining.qaDescription'),
        icon: <BsQuestionCircle />
      },
      googledocs: {
        title: t('dashboard.workspacetraining.googleDocsTitle'),
        description: t('dashboard.workspacetraining.googleDocsDescription'),
        icon: <BsGoogle />
      },
      notion: {
        title: t('dashboard.workspacetraining.notionTitle'),
        description: t('dashboard.workspacetraining.notionDescription'),
        icon: <NotionIcon size={20} />
      }
    };

    return headerConfigs[tab] || {
      title: t('dashboard.workspacetraining.genericTitle'),
      description: t('dashboard.workspacetraining.genericDescription'),
      icon: <FiCpu />
    };
  };

  const currentHeader = getHeaderContent(selectedTab);

  // Helper to re-trigger polling
  const restartPolling = () => setPollingKey((prev) => prev + 1);

  // Tooltip wrapper for disabled operations
  const wrapWithTooltip = (child) =>
    systemStatus !== 'OK' ? (
      <CustomTooltip
        content={t('dashboard.workspacetraining.tooltipSystemProcessing')}
        placement="top"
      >
        {child}
      </CustomTooltip>
    ) : (
      crawlUrlLimit && crawledUrlCount >= crawlUrlLimit ? (
        <CustomTooltip
          content={t('dashboard.workspacetraining.tooltipUrlLimitReached')}
          placement="top"
        >
          {child}
        </CustomTooltip>
      ) : (
        child
      )
    );


  // Poll the workspace state
  useEffect(() => {
    // Prevent multiple polling instances
    const pollingLockKey = `workspace_polling_${workspaceId}`;
    if (window[pollingLockKey]) {
      return; // Already polling for this workspace
    }
    window[pollingLockKey] = true;

    let isCancelled = false;
    let timeoutId = null;
    let shouldFetchTrainedData = false;

    const pollGetState = async () => {
      if (isCancelled) return;

      try {
        // Get workspace reference inside to avoid stale closure
        const currentWorkspaceRef = currentWorkspace;
        if (!currentWorkspaceRef?.workspace_id) return;

        // Get just workspace (includes state and workspace info) and update context
        const workspace = await getJustWorkspace(true);

        if (isCancelled) return;

        const data = {
          state: workspace?.state || 'OK',
          workspace_character_count: workspace?.workspace_character_count || workspaceData?.workspace_character_count || 0,
          workspace_bv_count: workspace?.workspace_bv_count || workspaceData?.workspace_bv_count || 0,
          workspace_crawl_url_count: workspace?.workspace_crawl_url_count || workspaceData?.workspace_crawl_url_count || 0,
          workspace_member_count: workspace?.workspace_member_count || workspaceData?.workspace_member_count || 0,
          workspace_message_count: workspace?.workspace_message_count || workspaceData?.workspace_message_count || 0
        };

        // Update character counts from polling data
        updateCharacterCounts(data);

        if (!stateLoadedRef.current) {
          stateLoadedRef.current = true;
          setStateLoading(false);
          // Initial load will be handled by the useEffect that watches selectedTab
        }

        if (isCancelled) return;

        // Store the previous state from ref and update it
        const prevState = previousSystemStatusRef.current;
        const hasStateChanged = data.state !== prevState;

        // Function to refresh quick start guide when state changes
        const refreshGuideIfStateChanged = () => {
          if (hasStateChanged && quickStartGuideFunctions?.refreshGuide) {
            quickStartGuideFunctions.refreshGuide();
          }
        };

        // Update the ref with the new state
        previousSystemStatusRef.current = data.state;

        let shouldContinuePolling = true;

        // Centralize system state updates.
        if (data.state.startsWith('ERROR')) {
          setSystemStatus(data.state);
          setReadyToTrain(false);
          setError(data.state);
          refreshGuideIfStateChanged();
          shouldContinuePolling = false;
        } else if (
          ['ADD', 'CREATION', 'QUEUE'].some((status) =>
            data.state.includes(status)
          )
        ) {
          setSystemStatus(data.state);
          setReadyToTrain(false);
          shouldFetchTrainedData = true;
          refreshGuideIfStateChanged();
        } else if (data.state === 'READY_TO_TRAIN') {
          setSystemStatus(data.state);
          setReadyToTrain(true);
          refreshGuideIfStateChanged();
          shouldContinuePolling = false;
        } else if (data.state === 'TRAIN') {
          setSystemStatus(data.state);
          shouldFetchTrainedData = true;
          refreshGuideIfStateChanged();
        } else if (data.state === 'DELETE_CONTENT') {
          setSystemStatus(data.state);
          shouldFetchTrainedData = true;
          refreshGuideIfStateChanged();
        } else if (data.state === 'SCHEDULE_TRAINING') {
          setSystemStatus(data.state);
          shouldFetchTrainedData = true;
          refreshGuideIfStateChanged();
        } else if (data.state === 'OK') {
          setSystemStatus(data.state);
          setReadyToTrain(false);
          refreshGuideIfStateChanged();
          if (shouldFetchTrainedData) {
            // Refresh the current tab's data after training completes
            refreshCurrentTabData();
          }
          shouldContinuePolling = false;
        }

        // Continue polling if needed
        if (shouldContinuePolling && !isCancelled) {
          timeoutId = setTimeout(pollGetState, 3000); // Increased to 3 seconds
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || 'An unknown error occurred');
          // Retry after longer delay on error
          timeoutId = setTimeout(pollGetState, 5000);
        }
      }
    };

    // Start polling
    pollGetState();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Clean up polling lock
      delete window[pollingLockKey];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pollingKey]);

  // Fetch the trained data for a specific category with pagination
  const fetchTrainedDataForCategory = async (contentType, page = 1, pageSize = 10) => {
    try {
      const categoryKey = contentType === 'link' ? 'web' : contentType;
      setIsTrainedDataLoading(true);
      const brandvoxToken = localStorage.getItem('brandvox_token');

      const response = await fetch(
        `${apiUrl}/api/v1/train/get-trained-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            workspace_id: workspaceId,
            content_type: contentType,
            page: page,
            page_size: pageSize
          }),
        }
      );
      if (!response.ok) throw new Error('Failed to fetch trained data');
      const data = await response.json();

      // Convert the trained_data object to array format
      const categorizedItems = [];
      Object.entries(data.trained_data).forEach(([entryKey, item]) => {
        if (item.type === contentType || (contentType === 'link' && item.type === 'web')) {
          if (!item.key) item.key = entryKey;
          categorizedItems.push(item);
        }
      });

      // Update trained data for this category
      setTrainedData(prev => ({
        ...prev,
        [categoryKey]: categorizedItems
      }));

      // Update pagination metadata
      setPaginationMetadata(prev => ({
        ...prev,
        [categoryKey]: {
          totalCount: data.total_count,
          totalPages: data.total_pages,
          currentPage: data.current_page
        }
      }));

    } catch (err) {
      setError(err.message || 'An unknown error occurred');
    } finally {
      setIsTrainedDataLoading(false);
      setLoading(false);
    }
  };

  // Poll queue position when systemStatus indicates queuing
  useEffect(() => {
    let interval;
    if (systemStatus.includes('QUEUE')) {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const fetchQueuePosition = async () => {
        try {
          const response = await fetch(
            `${apiUrl}/api/v1/train/get-workspace-training-queue-position?workspace_id=${workspaceId}`,
            { headers: { Authorization: `Bearer ${brandvoxToken}` } }
          );
          if (!response.ok)
            throw new Error('Failed to fetch queue position');
          const data = await response.json();
          if (data.queue_position === 'Workspace is not in the queue') {
            setQueuePosition(null);
          } else {
            setQueuePosition(data.queue_position);
          }
        } catch (err) {
          setError(err.message || 'An unknown error occurred');
        }
      };

      fetchQueuePosition();
      interval = setInterval(fetchQueuePosition, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [systemStatus, apiUrl, workspaceId]);

  // Fetch data when tab or page changes (NEW PAGINATION LOGIC)
  useEffect(() => {
    if (!workspaceId || !selectedTab) return;

    // Map tab name to content type
    const contentTypeMap = {
      'website': 'link',
      'files': 'file',
      'text': 'text',
      'qa': 'qa',
      'googledocs': 'googledoc',
      'notion': 'notion'
    };

    const contentType = contentTypeMap[selectedTab];
    if (contentType) {
      const categoryKey = contentType === 'link' ? 'web' : contentType;
      const page = currentPage[categoryKey] || 1;
      fetchTrainedDataForCategory(contentType, page, 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, workspaceId, currentPage]);

  // Helper to refresh current tab data (used after training completes)
  const refreshCurrentTabData = () => {
    if (!selectedTab) return;

    const contentTypeMap = {
      'website': 'link',
      'files': 'file',
      'text': 'text',
      'qa': 'qa',
      'googledocs': 'googledoc',
      'notion': 'notion'
    };

    const contentType = contentTypeMap[selectedTab];
    if (contentType) {
      const categoryKey = contentType === 'link' ? 'web' : contentType;
      const page = currentPage[categoryKey] || 1;
      fetchTrainedDataForCategory(contentType, page, 10);

      // Close expanded website dropdown when data is refreshed
      if (selectedTab === 'website') {
        setExpandedWebsiteKey(null);
      }
    }
  };

  // Update character count data from polling response
  const updateCharacterCounts = (chatbotData) => {
    if (chatbotData?.chatbot_character_count !== undefined) {
      // Treat null as 0
      setAgentCount(chatbotData.chatbot_character_count || 0);
    }
    if (chatbotData?.crawled_url_count !== undefined) {
      setCrawledUrlCount(chatbotData.crawled_url_count);
    }
    // Set counts loading to false once we have data
    setCountsLoading(false);
  };

  // Modal open handlers
  const openAddTextModal = () => setShowAddTextModal(true);
  const openAddFilesModal = () => setShowAddFilesModal(true);
  const openAddLinksModal = () => setShowAddLinksModal(true);
  const openAddWebsiteModal = () => setShowAddWebsiteModal(true);
  const openAddQAModal = () => setShowAddQAModal(true);
  const openAddGoogleDocsModal = () => setShowAddGoogleDocsModal(true);
  const openAddNotionModal = () => setShowAddNotionModal(true);

  // Assignment functions
  const openAssignmentModal = async () => {
    // Show loading spinner immediately
    setIsLoadingAssignments(true);

    try {
      // Compute common assignments before opening modal
      const commonAssignments = await getCommonAssignmentsForSelected();
      setInitialAssignments(commonAssignments);
      setShowAssignmentModal(true);
    } catch (error) {
      // TODO: Optionally show error toast to user
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const getSelectedItemsForAssignment = () => {
    return [
      ...selectedItems.text,
      ...selectedItems.file,
      ...selectedItems.web,
      ...selectedItems.qa,
      ...selectedItems.googledoc,
      ...selectedItems.notion
    ];
  };

  // Get common assignments across all selected items (intersection) - OPTIMIZED
  const getCommonAssignmentsForSelected = async () => {
    const selectedKeys = getSelectedItemsForAssignment();

    if (selectedKeys.length === 0) {
      return { chatbots: [], apps: [] };
    }

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Single optimized API call to get all assignments for selected items
      const response = await fetch(
        `${apiUrl}/api/v1/train/get-selected-assignments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            workspace_id: workspaceId,
            content_names: selectedKeys
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch selected assignments');
      }

      const data = await response.json();
      const assignments = data.assignments || {};

      // Build items with assignments array
      const itemsWithAssignments = [];

      for (const key of selectedKeys) {
        const assignment = assignments[key];
        if (assignment) {
          itemsWithAssignments.push({
            chatbots: assignment.chatbots || [],
            apps: assignment.apps || []
          });
        }
      }

      if (itemsWithAssignments.length === 0) {
        return { chatbots: [], apps: [] };
      }

      // Compute intersection - only include IDs present in ALL selected items
      const firstItem = itemsWithAssignments[0];

      const commonChatbots = firstItem.chatbots.filter(chatbotId =>
        itemsWithAssignments.every(assigned => assigned.chatbots.includes(chatbotId))
      );

      const commonApps = firstItem.apps.filter(appId =>
        itemsWithAssignments.every(assigned => assigned.apps.includes(appId))
      );

      return { chatbots: commonChatbots, apps: commonApps };

    } catch (error) {
      return { chatbots: [], apps: [] };
    }
  };

  const handleAssignmentSuccess = () => {
    restartPolling();
    setShowAssignmentModal(false);
    handleCancelEdit();
  };

  // Editing logic
  const handleEdit = (type, event) => {
    event.stopPropagation();
    setEditMode({ text: false, file: false, web: false, qa: false, googledoc: false, notion: false, [type]: true });
    setSelectedItems({ text: [], file: [], web: [], qa: [], googledoc: [], notion: [] });
  };

  const handleCancelEdit = (event) => {
    if (event) event.stopPropagation();
    setEditMode({ text: false, file: false, web: false, qa: false, googledoc: false, notion: false });
    setSelectedItems({ text: [], file: [], web: [], qa: [], googledoc: [], notion: [] });
  };

  const handleItemSelection = (type, key) => {
    setSelectedItems((prev) => {
      const currentSelection = prev[type].includes(key)
        ? prev[type].filter((item) => item !== key)
        : [...prev[type], key];
      return { ...prev, [type]: currentSelection };
    });
  };

  // Delete items
  const handleDelete = async (type) => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        content_names: selectedItems[type],
      });
      const response = await fetch(`${apiUrl}/api/v1/train/delete-contents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });
      if (!response.ok) throw new Error('Failed to delete content');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setEditMode({ text: false, file: false, web: false, qa: false, googledoc: false, notion: false });
      restartPolling();
    } catch (err) {
      setError(err.message || 'An unknown error occurred');
    }
  };

  // Pagination
  const handlePageChange = (type, page) => {
    setCurrentPage((prev) => ({ ...prev, [type]: page }));
  };

  // Search
  const handleSearchChange = (type, value) =>
    setSearchTerms((prev) => ({ ...prev, [type]: value }));

  // Filter data based on search
  const getFilteredData = (type) =>
    trainedData[type].filter(
      (item) =>
        item.key &&
        item.key.toLowerCase().includes(searchTerms[type].toLowerCase())
    );

  // Helper to get pagination props for a category
  const getPaginationProps = (categoryKey) => {
    return {
      totalPages: paginationMetadata[categoryKey]?.totalPages || 1,
      totalCount: paginationMetadata[categoryKey]?.totalCount || 0
    };
  };

  // Fetch ALL links for a website (unpaginated) for selection purposes
  const fetchAllLinksForWebsite = async (domainName) => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Fetch with a large page size to get all links at once
      const response = await fetch(
        `${apiUrl}/api/v1/train/get-website-links`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            workspace_id: workspaceId,
            domain_name: domainName,
            page: 1,
            page_size: 10000 // Large number to get all links
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch all website links');
      }

      const data = await response.json();
      return data.links || [];
    } catch (err) {
      return [];
    }
  };

  // Websites "Select All"
  const handleSelectAllWebsites = async (website) => {
    if (website) {
      // For single website: first fetch all links to check if they're all selected
      const domainKey = website.key;

      // Set loading for this specific domain
      setSelectAllLoading(prev => ({ ...prev, [domainKey]: true }));

      try {
        const allLinks = await fetchAllLinksForWebsite(domainKey);

        if (allLinks.length === 0) {
          setSelectAllLoading(prev => ({ ...prev, [domainKey]: false }));
          return;
        }

        const websiteLinks = allLinks.map(link => link.key);

        // Check if ALL links from this website are currently selected
        const allSelected = websiteLinks.every(key => selectedItems.web.includes(key));

        if (allSelected) {
          // Deselect all links of THIS website only
          setSelectedItems(prev => ({
            ...prev,
            web: prev.web.filter(k => !websiteLinks.includes(k))
          }));
        } else {
          // Select all links of this website (add to existing selections)
          setSelectedItems(prev => ({
            ...prev,
            web: Array.from(new Set([...prev.web, ...websiteLinks]))
          }));
        }
      } catch (error) {
      } finally {
        setSelectAllLoading(prev => ({ ...prev, [domainKey]: false }));
      }
    } else {
      // Select/Deselect all links across all websites

      // Check if already all selected (deselecting)
      if (isAllWebsitesSelected) {
        // Deselect all without fetching
        setSelectedItems((prev) => ({
          ...prev,
          web: [],
        }));
        return;
      }

      // User is selecting - show loading for "all websites" button
      setSelectAllLoading(prev => ({ ...prev, '__all__': true }));
      try {
        const filteredWebsites = getFilteredData('web');

        // Fetch all links for all websites
        const linkFetchPromises = filteredWebsites.map(website =>
          fetchAllLinksForWebsite(website.key)
        );

        const allLinksArrays = await Promise.all(linkFetchPromises);

        // Flatten all links into a single array of keys
        const allWebsiteKeys = allLinksArrays.flat().map(link => link.key);
        const uniqueWebsiteKeys = Array.from(new Set(allWebsiteKeys));

        // Select all
        setSelectedItems((prev) => ({
          ...prev,
          web: Array.from(new Set([...prev.web, ...uniqueWebsiteKeys])),
        }));
      } catch (error) {
      } finally {
        setSelectAllLoading(prev => ({ ...prev, '__all__': false }));
      }
    }
  };

  const handleUpdateLocalGuide = useCallback((guideFunctions) => {
    setQuickStartGuideFunctions(guideFunctions);
  }, []);

  // Check if all items are selected in each category
  // For websites, use pagination metadata to determine if all are selected
  // Compare total count from API with number of selected items
  const totalWebLinks = paginationMetadata.web.totalCount;
  const isAllWebsitesSelected =
    totalWebLinks > 0 && selectedItems.web.length >= totalWebLinks;

  // Close the TrainProcess modal
  const handleCloseTrainProcess = () => {
    setReadyToTrain(false);
  };

  // On training success, restart polling
  const handleTrainSuccess = async () => {
    restartPolling();
  };

  // Tour handlers
  const handleTourComplete = () => {
    localStorage.setItem(`workspace_training_tour_completed_${workspaceId}`, 'true');
    setShowTour(false);
  };

  const handleTourClose = () => {
    localStorage.setItem(`workspace_training_tour_completed_${workspaceId}`, 'true');
    setShowTour(false);
  };

  const handleRestartTour = () => {
    setShowTour(true);
  };

  // Check if tour should be shown on first visit
  useEffect(() => {
    if (!workspaceId) return;

    const tourCompleted = localStorage.getItem(`workspace_training_tour_completed_${workspaceId}`);

    // Show tour if not completed before and loading is complete
    if (!tourCompleted && !stateLoading && !isTrainedDataLoading && !countsLoading) {
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 500); // Small delay for better UX
      return () => clearTimeout(timer);
    }
  }, [workspaceId, stateLoading, isTrainedDataLoading, countsLoading]);

  // Check for Google connected parameter - only if Google Docs is enabled
  useEffect(() => {
    if (isGoogleDocsDisabled) return; // Skip Google Docs logic if disabled

    const query = new URLSearchParams(location.search);
    const isGoogleConnected = query.get('google_connected') === 'true';
    const isGoogleError = query.get('google_error') === 'true';
    const connectedEmail = query.get('connected_email');
    const errorMessage = query.get('error');

    if (isGoogleConnected) {
      // Store the email temporarily to pass to the modal
      if (connectedEmail) {
        localStorage.setItem('temp_connected_email', connectedEmail);
      }

      // Navigate to googledocs tab and open modal immediately if already on the right section
      if (section === 'googledocs') {
        // We're already on the googledocs section, open modal immediately
        setTimeout(() => {
          setShowAddGoogleDocsModal(true);
          if (connectedEmail) {
            localStorage.removeItem('temp_connected_email');
          }
        }, 100);
      } else {
        // Navigate to googledocs tab (the modal will be opened by the separate useEffect)
        navigate(`/training/googledocs`, { replace: true });
      }

      // Clean up URL parameters
      const newQuery = new URLSearchParams(location.search);
      newQuery.delete('google_connected');
      newQuery.delete('connected_email');
      const newSearch = newQuery.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    } else if (isGoogleError) {
      // Handle Google OAuth error
      const message = errorMessage || 'Failed to connect Google account';
      setError(message);

      // Navigate to googledocs tab to show the error
      navigate(`/training/googledocs`, { replace: true });

      // Clean up URL parameters
      const newQuery = new URLSearchParams(location.search);
      newQuery.delete('google_error');
      newQuery.delete('error');
      const newSearch = newQuery.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, '', newUrl);

      // Auto-clear error after 10 seconds
      setTimeout(() => {
        setError(null);
      }, 10000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isGoogleDocsDisabled, navigate, section]);

  // Check for Notion connected parameter - only if Notion is enabled
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const isNotionConnected = query.get('notion_connected') === 'true';
    const isNotionError = query.get('notion_error') === 'true';
    const connectedWorkspace = query.get('connected_workspace');
    const errorMessage = query.get('error');

    if (isNotionConnected) {
      // Store the workspace temporarily to pass to the modal
      if (connectedWorkspace) {
        localStorage.setItem('temp_connected_workspace', connectedWorkspace);
      }

      // Navigate to notion tab and open modal immediately if already on the right section
      if (section === 'notion') {
        // We're already on the notion section, open modal immediately
        setTimeout(() => {
          setShowAddNotionModal(true);
          if (connectedWorkspace) {
            localStorage.removeItem('temp_connected_workspace');
          }
        }, 100);
      } else {
        // Navigate to notion tab (the modal will be opened by the separate useEffect)
        navigate(`/training/notion`, { replace: true });
      }

      // Clean up URL parameters
      const newQuery = new URLSearchParams(location.search);
      newQuery.delete('notion_connected');
      newQuery.delete('connected_workspace');
      const newSearch = newQuery.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    } else if (isNotionError) {
      // Handle Notion OAuth error
      const message = errorMessage || 'Failed to connect Notion account';
      setError(message);

      // Navigate to notion tab to show the error
      navigate(`/training/notion`, { replace: true });

      // Clean up URL parameters
      const newQuery = new URLSearchParams(location.search);
      newQuery.delete('notion_error');
      newQuery.delete('error');
      const newSearch = newQuery.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, '', newUrl);

      // Auto-clear error after 10 seconds
      setTimeout(() => {
        setError(null);
      }, 10000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate, section]);

  // Check if we should open modal after landing on the specific section
  useEffect(() => {
    // Use setTimeout to ensure component is fully mounted before opening modal
    const timer = setTimeout(() => {
      // Check if we have a temp connected email and we're on googledocs section
      const tempEmail = localStorage.getItem('temp_connected_email');
      if (tempEmail && section === 'googledocs' && !showAddGoogleDocsModal) {
        setShowAddGoogleDocsModal(true);
        // Clean up the temp email after using it
        localStorage.removeItem('temp_connected_email');
      }

      // Check if we have a temp connected workspace and we're on notion section
      const tempWorkspace = localStorage.getItem('temp_connected_workspace');
      if (tempWorkspace && section === 'notion' && !showAddNotionModal) {
        setShowAddNotionModal(true);
        // Clean up the temp workspace after using it
        localStorage.removeItem('temp_connected_workspace');
      }
    }, 100); // Small delay to ensure component is ready

    return () => clearTimeout(timer);
  }, [section, showAddGoogleDocsModal, showAddNotionModal]);

  // Render Google Docs content
  const renderGoogleDocsContent = () => {
    if (isTrainedDataLoading) return null;
    if (trainedData.googledoc.length === 0) {
      return renderNoContentFallback(
        'Google Docs Training',
        openAddGoogleDocsModal,
        <BsGoogle size={64} />
      );
    }
    return (
      <>

        <div>
          <div className='d-flex align-items-center justify-content-between mb-4 content-action-buttons'>
            <div>
              {!editMode.googledoc && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  className="google-docs-btn"
                  onClick={openAddGoogleDocsModal}
                  disabled={systemStatus !== 'OK'}
                  width="100%"
                  backgroundColor="var(--google-primary)"
                  borderColor="var(--google-primary)"
                  hoverBackgroundColor="var(--google-secondary)"
                  hoverBorderColor="var(--google-secondary)"
                >
                  {t('dashboard.workspacetraining.addGoogleDocsButton')}
                </PrimaryButton>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAdmin && wrapWithTooltip(
                editMode.googledoc ? (
                  <CancelButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleCancelEdit(e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.cancelButton')}
                  </CancelButton>
                ) : (
                  <SuccessButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleEdit('googledoc', e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.editButton')}
                  </SuccessButton>
                )
              )}
              {editMode.googledoc && isAdmin &&
                wrapWithTooltip(
                  <DangerButton
                    disabled={
                      selectedItems.googledoc.length === 0 || systemStatus !== 'OK'
                    }
                    onClick={() => handleDelete('googledoc')}
                  >
                    {t('dashboard.workspacetraining.deleteSelectedButton', { count: selectedItems.googledoc.length })}
                  </DangerButton>
                )}
              {editMode.googledoc && isAdmin && (
                <PrimaryButton
                  onClick={() => openAssignmentModal()}
                  disabled={selectedItems.googledoc.length === 0 || systemStatus !== 'OK' || isLoadingAssignments}
                >
                  {isLoadingAssignments ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('dashboard.workspacetraining.loadingAssignments')}
                    </>
                  ) : (
                    t('dashboard.workspacetraining.manageAssignmentsButton')
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Search controls */}
          <Form.Group className='mb-3'>
            <Form.Control
              type='text'
              value={searchTerms.googledoc}
              onChange={(e) => handleSearchChange('googledoc', e.target.value)}
              placeholder={t('dashboard.workspacetraining.searchGoogleDocs')}
              className='search-input'
            />
          </Form.Group>

          <TrainingList
            type='googledoc'
            data={getFilteredData('googledoc')}
            currentPage={currentPage.googledoc}
            onPageChange={handlePageChange}
            editMode={editMode.googledoc}
            selectedItems={selectedItems.googledoc}
            onItemSelect={(key) => handleItemSelection('googledoc', key)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            {...getPaginationProps('googledoc')}
          />
        </div>
      </>
    );
  };

  // Render Notion content
  const renderNotionContent = () => {
    if (isTrainedDataLoading) return null;
    if (trainedData.notion.length === 0) {
      return renderNoContentFallback(
        'Notion Training',
        openAddNotionModal,
        <NotionIcon size={64} />
      );
    }
    return (
      <>

        <div>
          <div className='d-flex align-items-center justify-content-between mb-4 content-action-buttons'>
            <div>
              {!editMode.notion && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  className="notion-btn"
                  onClick={openAddNotionModal}
                  width="100%"
                  disabled={systemStatus !== 'OK'}
                  backgroundColor="var(--notion-primary)"
                  borderColor="var(--notion-primary)"
                  hoverBackgroundColor="var(--notion-secondary)"
                  hoverBorderColor="var(--notion-secondary)"
                >
                  {t('dashboard.workspacetraining.addNotionButton')}
                </PrimaryButton>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAdmin && wrapWithTooltip(
                editMode.notion ? (
                  <CancelButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleCancelEdit(e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.cancelButton')}
                  </CancelButton>
                ) : (
                  <SuccessButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleEdit('notion', e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.editButton')}
                  </SuccessButton>
                )
              )}
              {editMode.notion && isAdmin &&
                wrapWithTooltip(
                  <DangerButton
                    disabled={
                      selectedItems.notion.length === 0 || systemStatus !== 'OK'
                    }
                    onClick={() => handleDelete('notion')}
                  >
                    {t('dashboard.workspacetraining.deleteSelectedButton', { count: selectedItems.notion.length })}
                  </DangerButton>
                )}
              {editMode.notion && isAdmin && (
                <PrimaryButton
                  onClick={() => openAssignmentModal()}
                  disabled={selectedItems.notion.length === 0 || systemStatus !== 'OK' || isLoadingAssignments}
                >
                  {isLoadingAssignments ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('dashboard.workspacetraining.loadingAssignments')}
                    </>
                  ) : (
                    t('dashboard.workspacetraining.manageAssignmentsButton')
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Search controls */}
          <Form.Group className='mb-3'>
            <Form.Control
              type='text'
              value={searchTerms.notion}
              onChange={(e) => handleSearchChange('notion', e.target.value)}
              placeholder={t('dashboard.workspacetraining.searchNotion')}
              className='search-input'
            />
          </Form.Group>

          <TrainingList
            type='notion'
            data={getFilteredData('notion')}
            currentPage={currentPage.notion}
            onPageChange={handlePageChange}
            editMode={editMode.notion}
            selectedItems={selectedItems.notion}
            onItemSelect={(key) => handleItemSelection('notion', key)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            {...getPaginationProps('notion')}
          />
        </div>
      </>
    );
  };

  // Fallback when no websites
  const renderNoWebsiteFallback = () => (
    <div className="empty-state-container brandvox-empty text-center p-5">
      {crawlUrlLimit && crawledUrlCount >= crawlUrlLimit && (
        <CompactUpgradePrompt
          message={t('dashboard.workspacetraining.upgradeMessage')}
          buttonText={t('dashboard.workspacetraining.upgradeButton')}
          type="quota"
          percentage={100}
        />
      )}
      <div className="empty-state-icon mb-4">
        <BsGlobe size={64} className="brandvox-icon" />
      </div>
      <h4>{t('dashboard.workspacetraining.emptyWebsiteTitle')}</h4>
      <p className="text-muted mb-4">
        {t('dashboard.workspacetraining.emptyWebsiteDescription')}
      </p>
      {isAdmin && (
        <div className="d-flex justify-content-center">
          {wrapWithTooltip(
            <PrimaryButton
              className="me-3"
              onClick={openAddWebsiteModal}
              disabled={systemStatus !== 'OK' || (crawlUrlLimit && crawledUrlCount >= crawlUrlLimit)}
            >
              {t('dashboard.workspacetraining.addWebsiteButton')}
            </PrimaryButton>
          )}
          {wrapWithTooltip(
            <PrimaryButton
              onClick={openAddLinksModal}
              disabled={systemStatus !== 'OK' || (crawlUrlLimit && crawledUrlCount >= crawlUrlLimit)}
            >
              {t('dashboard.workspacetraining.addLinksButton')}
            </PrimaryButton>
          )}
        </div>
      )}
    </div>
  );

  // Generic fallback for empty tabs
  const renderNoContentFallback = (tabName, onClickAdd, icon) => {
    // Determine the container class based on the tab
    const containerClass = tabName === 'Google Docs Training' ? 'google-docs-empty' :
      tabName === 'Notion Training' ? 'notion-empty' : 'brandvox-empty';

    const tabTitle = tabName === 'File Training' ? t('dashboard.workspacetraining.emptyFilesTitle') :
      tabName === 'Text Training' ? t('dashboard.workspacetraining.emptyTextTitle') :
        tabName === 'Google Docs Training' ? t('dashboard.workspacetraining.emptyGoogleDocsTitle') :
          tabName === 'Notion Training' ? t('dashboard.workspacetraining.emptyNotionTitle') :
            t('dashboard.workspacetraining.emptyQATitle');

    const tabDescription = tabName === 'File Training' ? t('dashboard.workspacetraining.emptyFilesDescription') :
      tabName === 'Text Training' ? t('dashboard.workspacetraining.emptyTextDescription') :
        tabName === 'Google Docs Training' ? t('dashboard.workspacetraining.emptyGoogleDocsDescription') :
          tabName === 'Notion Training' ? t('dashboard.workspacetraining.emptyNotionDescription') :
            t('dashboard.workspacetraining.emptyQADescription');

    const buttonText = tabName === 'File Training' ? t('dashboard.workspacetraining.addFilesButton') :
      tabName === 'Text Training' ? t('dashboard.workspacetraining.addTextButton') :
        tabName === 'Google Docs Training' ? t('dashboard.workspacetraining.addGoogleDocsButton') :
          tabName === 'Notion Training' ? t('dashboard.workspacetraining.addNotionButton') :
            t('dashboard.workspacetraining.addQAButton');

    return (
      <div className={`empty-state-container text-center p-5 ${containerClass}`}>
        <div className="empty-state-icon mb-4">
          {React.cloneElement(icon, {
            className: `${icon.props.className || ''} ${tabName === 'Google Docs Training' ? 'google-docs-icon' :
              tabName === 'Notion Training' ? 'notion-icon' :
                'brandvox-icon'
              }`
          })}
        </div>
        <h4>{tabTitle}</h4>
        <p className="text-muted mb-4">
          {tabDescription}
        </p>
        {isAdmin && wrapWithTooltip(
          <PrimaryButton
            onClick={onClickAdd}
            disabled={systemStatus !== 'OK'}
            backgroundColor={
              tabName === 'Google Docs Training' ? 'var(--google-primary)' :
                tabName === 'Notion Training' ? 'var(--notion-primary)' :
                  'var(--gradient-primary)'
            }
            borderColor={
              tabName === 'Google Docs Training' ? 'var(--google-primary)' :
                tabName === 'Notion Training' ? 'var(--notion-primary)' :
                  'var(--primary)'
            }
            hoverBackgroundColor={
              tabName === 'Google Docs Training' ? 'var(--google-secondary)' :
                tabName === 'Notion Training' ? 'var(--notion-secondary)' :
                  'var(--primary-hover)'
            }
            hoverBorderColor={
              tabName === 'Google Docs Training' ? 'var(--google-secondary)' :
                tabName === 'Notion Training' ? 'var(--notion-secondary)' :
                  'var(--primary-hover)'
            }
          >
            {buttonText}
          </PrimaryButton>
        )}
      </div>
    );
  };

  // Render website content
  const renderWebsiteContent = () => {
    if (isTrainedDataLoading) return null;
    if (trainedData.web.length === 0) {
      return renderNoWebsiteFallback();
    }
    return (
      <>
        <div>
          <div className='d-flex align-items-center justify-content-between mb-4 content-action-buttons'>
            <div className="add-content-buttons">
              {!editMode.web && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  onClick={openAddWebsiteModal}
                  width="100%"
                  disabled={systemStatus !== 'OK' || (crawlUrlLimit && crawledUrlCount >= crawlUrlLimit)}
                >
                  {t('dashboard.workspacetraining.addWebsiteButton')}
                </PrimaryButton>
              )}
              {!editMode.web && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  onClick={openAddLinksModal}
                  width="100%"
                  disabled={systemStatus !== 'OK' || (crawlUrlLimit && crawledUrlCount >= crawlUrlLimit)}
                >
                  {t('dashboard.workspacetraining.addLinksButton')}
                </PrimaryButton>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAdmin && wrapWithTooltip(
                editMode.web ? (
                  <CancelButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleCancelEdit(e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.cancelButton')}
                  </CancelButton>
                ) : (
                  <SuccessButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleEdit('web', e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.editButton')}
                  </SuccessButton>
                )
              )}
              {editMode.web && isAdmin &&
                wrapWithTooltip(
                  <DangerButton
                    disabled={
                      selectedItems.web.length === 0 || systemStatus !== 'OK'
                    }
                    onClick={() => handleDelete('web')}
                  >
                    {t('dashboard.workspacetraining.deleteSelectedButton', { count: selectedItems.web.length })}
                  </DangerButton>
                )}
              {editMode.web && isAdmin && (
                <PrimaryButton
                  onClick={() => openAssignmentModal()}
                  disabled={selectedItems.web.length === 0 || systemStatus !== 'OK' || isLoadingAssignments}
                >
                  {isLoadingAssignments ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('dashboard.workspacetraining.loadingAssignments')}
                    </>
                  ) : (
                    t('dashboard.workspacetraining.manageAssignmentsButton')
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Search controls */}
          <Form.Group className='mb-3'>
            <Form.Control
              type='text'
              value={searchTerms.web}
              onChange={(e) => handleSearchChange('web', e.target.value)}
              placeholder={t('dashboard.workspacetraining.searchWebsites')}
              className='search-input'
            />
          </Form.Group>

          <TrainingWebsites
            data={getFilteredData('web')}
            currentPage={currentPage.web}
            onPageChange={handlePageChange}
            editMode={editMode.web}
            selectedItems={selectedItems.web}
            onItemSelect={(key) => handleItemSelection('web', key)}
            websitePage={websitePage}
            onWebsitePageChange={setWebsitePage}
            websiteLinkSearch={websiteLinkSearch}
            onWebsiteLinkSearch={setWebsiteLinkSearch}
            expandedWebsiteKey={expandedWebsiteKey}
            onWebsiteToggle={setExpandedWebsiteKey}
            onWebsiteSelect={handleSelectAllWebsites}
            isAllWebsitesSelected={isAllWebsitesSelected}
            selectAllLoading={selectAllLoading}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
            onFetchTrainedData={refreshCurrentTabData}
            systemStatus={systemStatus}
            crawledUrlCount={crawledUrlCount}
            crawlUrlLimit={crawlUrlLimit}
            userData={userData}
          />
        </div>
      </>
    );
  };

  // Render files content
  const renderFilesContent = () => {
    if (isTrainedDataLoading) return null;
    if (trainedData.file.length === 0) {
      return renderNoContentFallback(
        'File Training',
        openAddFilesModal,
        <BsFolder size={64} />
      );
    }
    return (
      <>
        <div>
          <div className='d-flex align-items-center justify-content-between mb-4 content-action-buttons'>
            <div>
              {!editMode.file && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  width="100%"
                  onClick={openAddFilesModal}
                  disabled={systemStatus !== 'OK'}
                >
                  {t('dashboard.workspacetraining.addFilesButton')}
                </PrimaryButton>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAdmin && wrapWithTooltip(
                editMode.file ? (
                  <CancelButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleCancelEdit(e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.cancelButton')}
                  </CancelButton>
                ) : (
                  <SuccessButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleEdit('file', e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.editButton')}
                  </SuccessButton>
                )
              )}
              {editMode.file && isAdmin &&
                wrapWithTooltip(
                  <DangerButton
                    disabled={
                      selectedItems.file.length === 0 || systemStatus !== 'OK'
                    }
                    onClick={() => handleDelete('file')}
                  >
                    {t('dashboard.workspacetraining.deleteSelectedButton', { count: selectedItems.file.length })}
                  </DangerButton>
                )}
              {editMode.file && isAdmin && (
                <PrimaryButton
                  onClick={() => openAssignmentModal()}
                  disabled={selectedItems.file.length === 0 || systemStatus !== 'OK' || isLoadingAssignments}
                >
                  {isLoadingAssignments ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('dashboard.workspacetraining.loadingAssignments')}
                    </>
                  ) : (
                    t('dashboard.workspacetraining.manageAssignmentsButton')
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Search controls */}
          <Form.Group className='mb-3'>
            <Form.Control
              type='text'
              value={searchTerms.file}
              onChange={(e) => handleSearchChange('file', e.target.value)}
              placeholder={t('dashboard.workspacetraining.searchFiles')}
              className='search-input'
            />
          </Form.Group>

          <TrainingList
            type='file'
            data={getFilteredData('file')}
            currentPage={currentPage.file}
            onPageChange={handlePageChange}
            editMode={editMode.file}
            selectedItems={selectedItems.file}
            onItemSelect={(key) => handleItemSelection('file', key)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            {...getPaginationProps('file')}
          />
        </div>
      </>
    );
  };

  // Render text content
  const renderTextContent = () => {
    if (isTrainedDataLoading) return null;
    if (trainedData.text.length === 0) {
      return renderNoContentFallback(
        'Text Training',
        openAddTextModal,
        <BsFileText size={64} />
      );
    }
    return (
      <>
        <div>
          <div className='d-flex align-items-center justify-content-between mb-4 content-action-buttons'>
            <div>
              {!editMode.text && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  onClick={openAddTextModal}
                  width="100%"
                  disabled={systemStatus !== 'OK'}
                >
                  {t('dashboard.workspacetraining.addTextButton')}
                </PrimaryButton>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAdmin && wrapWithTooltip(
                editMode.text ? (
                  <CancelButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleCancelEdit(e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.cancelButton')}
                  </CancelButton>
                ) : (
                  <SuccessButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleEdit('text', e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.editButton')}
                  </SuccessButton>
                )
              )}
              {editMode.text && isAdmin &&
                wrapWithTooltip(
                  <DangerButton
                    disabled={
                      selectedItems.text.length === 0 || systemStatus !== 'OK'
                    }
                    onClick={() => handleDelete('text')}
                  >
                    {t('dashboard.workspacetraining.deleteSelectedButton', { count: selectedItems.text.length })}
                  </DangerButton>
                )}
              {editMode.text && isAdmin && (
                <PrimaryButton
                  onClick={() => openAssignmentModal()}
                  disabled={selectedItems.text.length === 0 || systemStatus !== 'OK' || isLoadingAssignments}
                >
                  {isLoadingAssignments ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('dashboard.workspacetraining.loadingAssignments')}
                    </>
                  ) : (
                    t('dashboard.workspacetraining.manageAssignmentsButton')
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Search controls */}
          <Form.Group className='mb-3'>
            <Form.Control
              type='text'
              value={searchTerms.text}
              onChange={(e) => handleSearchChange('text', e.target.value)}
              placeholder={t('dashboard.workspacetraining.searchText')}
              className='search-input'
            />
          </Form.Group>

          <TrainingList
            type='text'
            data={getFilteredData('text')}
            currentPage={currentPage.text}
            onPageChange={handlePageChange}
            editMode={editMode.text}
            selectedItems={selectedItems.text}
            onItemSelect={(key) => handleItemSelection('text', key)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            {...getPaginationProps('text')}
          />

        </div>
      </>
    );
  };

  // Render Q&A content
  const renderQAContent = () => {
    if (isTrainedDataLoading) return null;
    if (trainedData.qa.length === 0) {
      return renderNoContentFallback(
        'Q&A Training',
        openAddQAModal,
        <BsQuestionCircle size={64} />
      );
    }
    return (
      <>
        <div>
          <div className='d-flex align-items-center justify-content-between mb-4 content-action-buttons'>
            <div>
              {!editMode.qa && isAdmin && wrapWithTooltip(
                <PrimaryButton
                  onClick={openAddQAModal}
                  width="100%"
                  disabled={systemStatus !== 'OK'}
                >
                  {t('dashboard.workspacetraining.addQAButton')}
                </PrimaryButton>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAdmin && wrapWithTooltip(
                editMode.qa ? (
                  <CancelButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleCancelEdit(e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.cancelButton')}
                  </CancelButton>
                ) : (
                  <SuccessButton
                    onClick={(e) => {
                      if (systemStatus !== 'OK') return;
                      handleEdit('qa', e);
                    }}
                    disabled={systemStatus !== 'OK'}
                  >
                    {t('dashboard.workspacetraining.editButton')}
                  </SuccessButton>
                )
              )}
              {editMode.qa && isAdmin &&
                wrapWithTooltip(
                  <DangerButton
                    disabled={
                      selectedItems.qa.length === 0 || systemStatus !== 'OK'
                    }
                    onClick={() => handleDelete('qa')}
                  >
                    {t('dashboard.workspacetraining.deleteSelectedButton', { count: selectedItems.qa.length })}
                  </DangerButton>
                )}
              {editMode.qa && isAdmin && (
                <PrimaryButton
                  onClick={() => openAssignmentModal()}
                  disabled={selectedItems.qa.length === 0 || systemStatus !== 'OK' || isLoadingAssignments}
                >
                  {isLoadingAssignments ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('dashboard.workspacetraining.loadingAssignments')}
                    </>
                  ) : (
                    t('dashboard.workspacetraining.manageAssignmentsButton')
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Search controls */}
          <Form.Group className='mb-3'>
            <Form.Control
              type='text'
              value={searchTerms.qa}
              onChange={(e) => handleSearchChange('qa', e.target.value)}
              placeholder={t('dashboard.workspacetraining.searchQA')}
              className='search-input'
            />
          </Form.Group>

          <TrainingList
            type='qa'
            data={getFilteredData('qa')}
            currentPage={currentPage.qa}
            onPageChange={handlePageChange}
            editMode={editMode.qa}
            selectedItems={selectedItems.qa}
            onItemSelect={(key) => handleItemSelection('qa', key)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            {...getPaginationProps('qa')}
          />
        </div>
      </>
    );
  };

  // If any data is still loading, show skeleton loader.
  if (stateLoading || isTrainedDataLoading || countsLoading) {
    return (
      <WorkspaceTrainingSkeleton
        pageHeader={
          <PageHeader
            icon={currentHeader.icon}
            title={currentHeader.title}
            description={currentHeader.description}
            hideMobile={false}
            hideDescriptionOnMobile={false}
          />
        }
        contentType={selectedTab === 'website' ? 'website' : 'default'}
      />
    );
  }

  return (
    <div className='agent-training-container'>
      <PageHeader
        icon={currentHeader.icon}
        title={currentHeader.title}
        description={currentHeader.description}
        hideMobile={false}
        hideDescriptionOnMobile={false}
        secondaryButton={{
          onClick: handleRestartTour,
          className: 'header-tour-btn',
          hideMobile: true,
          children: (
            <>
              <FiInfo size={16} className="me-2" />
              {t('dashboard.workspacetraining.tutorialButton')}
            </>
          )
        }}
      />

      <div className="training-content">
        <div className="d-block d-md-none training-sidebar">
          {/* Mobile - QuickStartGuide above Character Count */}
          <QuickStartGuide
            onUpdateLocalGuide={handleUpdateLocalGuide}
            workspaceData={workspaceData}
          />
          {/* Mobile - Character Count display above tabs */}
          <WorkspaceInfoPanel />
        </div>
        <div className="system-status-message">
          <SystemStatusMessage
            systemStatus={systemStatus}
            loading={loading}
            queuePosition={queuePosition}
            apiUrl={apiUrl}
            workspaceId={workspaceId}
            onStatusUpdate={restartPolling}
          />
        </div>
        <div className='training-tab-content'>
          {/* TrainProcess modal - only show for admin users */}
          {isAdmin && (
            <Modal
              show={readyToTrain}
              onHide={handleCloseTrainProcess}
              backdrop='static'
              keyboard={false}
              dialogClassName='modal-lg'
            >
              <Modal.Body style={{ padding: '12px 0' }}>
                <TrainProcess
                  workspaceId={workspaceId}
                  apiUrl={apiUrl}
                  onClose={handleCloseTrainProcess}
                  onTrainSuccess={handleTrainSuccess}
                  onPollState={restartPolling}
                />
              </Modal.Body>
            </Modal>
          )}

          {/* "Add" modals */}
          <AddTextModal
            show={showAddTextModal}
            onHide={() => setShowAddTextModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
          />
          <AddFilesModal
            show={showAddFilesModal}
            onHide={() => setShowAddFilesModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
          />
          <AddLinksModal
            show={showAddLinksModal}
            onHide={() => setShowAddLinksModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
            crawledUrlCount={crawledUrlCount}
            crawlUrlLimit={crawlUrlLimit}
          />
          <AddWebsiteModal
            show={showAddWebsiteModal}
            onHide={() => setShowAddWebsiteModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
            crawledUrlCount={crawledUrlCount}
            crawlUrlLimit={crawlUrlLimit}
          />
          <AddQAModal
            show={showAddQAModal}
            onHide={() => setShowAddQAModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
          />
          <AddGoogleDocsModal
            show={showAddGoogleDocsModal && !isGoogleDocsDisabled}
            onHide={() => setShowAddGoogleDocsModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
          />
          <AddNotionModal
            show={showAddNotionModal}
            onHide={() => setShowAddNotionModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            onPollState={restartPolling}
          />

          <AssignmentModal
            show={showAssignmentModal}
            onHide={() => setShowAssignmentModal(false)}
            workspaceId={workspaceId}
            apiUrl={apiUrl}
            selectedContents={getSelectedItemsForAssignment()}
            initialAssignments={initialAssignments}
            onAssignSuccess={handleAssignmentSuccess}
          />

          <div className="training-layout">
            <div className="training-main-content">
              {/* Render content based on selected tab */}
              {selectedTab === 'website' && renderWebsiteContent()}
              {selectedTab === 'files' && renderFilesContent()}
              {selectedTab === 'text' && renderTextContent()}
              {selectedTab === 'qa' && renderQAContent()}
              {selectedTab === 'googledocs' && !isGoogleDocsDisabled && renderGoogleDocsContent()}
              {selectedTab === 'notion' && renderNotionContent()}
            </div>

            {/* Sidebar - Desktop only */}
            <div className="training-sidebar d-none d-lg-block">
              <WorkspaceInfoPanel />
              <QuickStartGuide
                onUpdateLocalGuide={handleUpdateLocalGuide}
                workspaceData={workspaceData}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Workspace Training Tour */}
      <WorkspaceTrainingTour
        show={showTour}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
      />
    </div >
  );
};

export default WorkspaceTraining;
